import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exerciseBankService, type ExerciseSet } from "@/services/exerciseBankService";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, 
  ChevronRight, 
  CheckCircle2, 
  XCircle, 
  Award,
  Loader2,
  AlertTriangle,
  Lightbulb,
  RotateCcw
} from "lucide-react";

export default function ExercisePlayer() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [exerciseSet, setExerciseSet] = useState<ExerciseSet | null>(null);
  const [loading, setLoading] = useState(true);

  // Exercise Player States
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [answersHistory, setAnswersHistory] = useState<{ [qIdx: number]: { selected: string; isCorrect: boolean } }>({});
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    if (!id) return;
    try {
      setLoading(true);
      const set = exerciseBankService.getExerciseSetById(id);
      setExerciseSet(set);
    } catch (err) {
      console.error("Error loading exercise set:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
        <h4 className="font-bold text-slate-700 text-base">Carregando exercício...</h4>
      </div>
    );
  }

  if (!exerciseSet) {
    return (
      <div className="py-24 text-center text-slate-500 max-w-md mx-auto space-y-4">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-2" />
        <h3 className="text-lg font-bold">Exercício não encontrado</h3>
        <p className="text-xs font-semibold leading-relaxed">
          O conjunto de exercícios que você tentou acessar não foi localizado no banco de dados local.
        </p>
        <Button variant="outline" onClick={() => navigate("/exercises")} className="mt-4 rounded-xl">
          Voltar ao Catálogo
        </Button>
      </div>
    );
  }

  const questions = exerciseSet.questions;
  const currentQuestion = questions[currentIdx];
  const progressPercent = Math.round((currentIdx / questions.length) * 100);

  const handleSelectOption = (option: string) => {
    if (isAnswered) return;
    setSelectedOption(option);
  };

  const handleCheckAnswer = () => {
    if (selectedOption === null || isAnswered) return;

    const correctOption = currentQuestion.options[currentQuestion.correct_index];
    const isCorrect = selectedOption === correctOption;

    if (isCorrect) {
      setCorrectAnswersCount(prev => prev + 1);
    }

    setAnswersHistory(prev => ({
      ...prev,
      [currentIdx]: { selected: selectedOption, isCorrect }
    }));

    setIsAnswered(true);
  };

  const handleNextQuestion = () => {
    setSelectedOption(null);
    setIsAnswered(false);

    if (currentIdx < questions.length - 1) {
      setCurrentIdx(prev => prev + 1);
    } else {
      setIsFinished(true);
    }
  };

  const handleRestart = () => {
    setCurrentIdx(0);
    setSelectedOption(null);
    setIsAnswered(false);
    setCorrectAnswersCount(0);
    setAnswersHistory({});
    setIsFinished(false);
  };

  // Render Result Screen
  if (isFinished) {
    const scorePercent = Math.round((correctAnswersCount / questions.length) * 100);
    return (
      <div className="max-w-xl mx-auto space-y-8 py-12 animate-in fade-in duration-300 text-left">
        <Card className="border border-slate-100 rounded-3xl overflow-hidden bg-white shadow-sm">
          <CardHeader className="text-center p-8 bg-slate-50/30 border-b border-slate-100/50 space-y-3">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto text-primary shadow-3xs">
              <Award className="w-8 h-8 stroke-[1.75]" />
            </div>
            <div className="space-y-1">
              <span className="text-[10px] text-primary font-black uppercase tracking-wider">Résultats</span>
              <CardTitle className="text-xl md:text-2xl font-extrabold text-slate-900 leading-tight">
                Exercice Terminé !
              </CardTitle>
            </div>
            <CardDescription className="text-xs font-semibold text-slate-500 italic max-w-sm mx-auto">
              "{exerciseSet.title}"
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8 space-y-6">
            {/* Score block */}
            <div className="flex items-center justify-between p-5 rounded-2xl border border-slate-100 bg-slate-50/10 shadow-3xs">
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Pontuação Final</span>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-extrabold text-slate-900 leading-none">{correctAnswersCount}</span>
                  <span className="text-slate-400 text-sm font-semibold">/ {questions.length} questões</span>
                </div>
              </div>

              <div className="text-right">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-1">Aproveitamento</span>
                <span className={`text-xl font-extrabold px-3 py-1 rounded-xl shadow-3xs ${
                  scorePercent >= 70 
                    ? "bg-emerald-50 text-emerald-700" 
                    : scorePercent >= 50 
                      ? "bg-amber-50 text-amber-700" 
                      : "bg-rose-50 text-rose-700"
                }`}>
                  {scorePercent}%
                </span>
              </div>
            </div>

            {/* Questions Summary List */}
            <div className="space-y-3 pt-2">
              <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider">Resumo das Questões</h4>
              <div className="space-y-2">
                {questions.map((q, idx) => {
                  const hist = answersHistory[idx];
                  return (
                    <div 
                      key={idx} 
                      className="p-3.5 rounded-xl border border-slate-100 bg-white flex items-center justify-between gap-4 shadow-3xs"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-6 h-6 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-[10px] shrink-0 shadow-3xs">
                          {idx + 1}
                        </span>
                        <p className="font-bold text-slate-800 text-xs md:text-sm truncate">
                          {q.text}
                        </p>
                      </div>

                      {hist?.isCorrect ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
              <Button
                onClick={handleRestart}
                variant="outline"
                className="flex-1 h-11 border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-xs gap-1.5 shadow-3xs transition-all"
              >
                <RotateCcw className="w-4 h-4 text-slate-400" />
                Refazer Exercício
              </Button>
              <Button
                onClick={() => navigate("/exercises")}
                className="flex-1 h-11 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs gap-1.5 shadow-xs transition-all"
              >
                Voltar ao Catálogo
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Current answer index for display helper
  const isCorrectAnswer = selectedOption === currentQuestion.options[currentQuestion.correct_index];

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-24 animate-in fade-in duration-300 text-left">
      
      {/* Top Header with Back button and progress */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/exercises")} 
          className="rounded-xl border border-slate-200 text-slate-600 hover:text-slate-900 gap-1.5 h-10 px-4 bg-white shadow-3xs"
        >
          <ArrowLeft className="w-4 h-4 text-slate-400" />
          Voltar
        </Button>

        <div className="flex items-center gap-2">
          <Badge className="bg-slate-100 hover:bg-slate-100 text-slate-600 border-none font-bold text-[8px] tracking-wide uppercase px-2.5 py-0.5 rounded shadow-3xs">
            {exerciseSet.category}
          </Badge>
          <Badge className="bg-primary/5 hover:bg-primary/5 text-primary border-none font-bold text-[8px] tracking-wide uppercase px-2.5 py-0.5 rounded shadow-3xs">
            {exerciseSet.level}
          </Badge>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          <span>Progresso do Exercício</span>
          <span>{currentIdx + 1} de {questions.length} questões</span>
        </div>
        <Progress value={progressPercent} className="h-1.5 bg-slate-100 rounded-full" />
      </div>

      {/* Question Card */}
      <Card className="border border-slate-100 rounded-3xl overflow-hidden bg-white shadow-xs">
        <CardHeader className="p-6 md:p-8 border-b border-slate-100/50 bg-slate-50/20 space-y-2">
          <span className="text-[10px] text-primary font-black uppercase tracking-wider">Questão {currentIdx + 1}</span>
          <CardTitle className="text-base md:text-lg font-extrabold text-slate-800 leading-relaxed">
            {currentQuestion.text}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 md:p-8 space-y-6">
          {/* Options Grid */}
          <div className="grid gap-3">
            {currentQuestion.options.map((opt, oIdx) => {
              const isSelected = selectedOption === opt;
              const isCorrectOption = oIdx === currentQuestion.correct_index;
              
              let optStyle = "border-slate-200 text-slate-700 bg-white hover:border-primary hover:bg-slate-50/50";
              if (isSelected && !isAnswered) {
                optStyle = "border-primary bg-primary/5 text-primary shadow-3xs";
              } else if (isAnswered) {
                if (isCorrectOption) {
                  optStyle = "border-emerald-500 bg-emerald-50 text-emerald-800 shadow-3xs";
                } else if (isSelected && !isCorrectAnswer) {
                  optStyle = "border-red-500 bg-red-50 text-red-800 opacity-90 shadow-3xs";
                } else {
                  optStyle = "border-slate-100 opacity-50 text-slate-400 cursor-not-allowed";
                }
              }

              return (
                <button
                  key={oIdx}
                  onClick={() => handleSelectOption(opt)}
                  disabled={isAnswered}
                  className={`group w-full text-left p-4 rounded-2xl border font-semibold text-xs md:text-sm transition-all duration-200 ease-in-out flex items-center justify-between ${optStyle}`}
                >
                  <div className="flex items-center gap-3 text-left w-full min-w-0">
                    {/* Custom radio circle */}
                    <div className={`w-4.5 h-4.5 rounded-full border-2 shrink-0 flex items-center justify-center transition-all duration-200 ${
                      isSelected 
                        ? (isAnswered 
                            ? (isCorrectOption ? "border-emerald-600 bg-emerald-600" : "border-red-600 bg-red-600")
                            : "border-primary bg-primary") 
                        : (isAnswered && isCorrectOption 
                            ? "border-emerald-600 bg-emerald-600" 
                            : "border-slate-300 bg-white group-hover:border-primary/50")
                    }`}>
                      {(isSelected || (isAnswered && isCorrectOption)) && (
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </div>
                    <span className="text-slate-900 font-extrabold leading-relaxed text-left break-words w-full">{opt}</span>
                  </div>

                  {isAnswered && isCorrectOption && (
                    <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 shrink-0" />
                  )}
                  {isAnswered && isSelected && !isCorrectAnswer && (
                    <XCircle className="w-4.5 h-4.5 text-red-500 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Feedback & Actions */}
          <div className="pt-2">
            {!isAnswered ? (
              <Button
                onClick={handleCheckAnswer}
                disabled={selectedOption === null}
                className="w-full h-11 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs px-5 shadow-xs transition-all"
              >
                Verificar Resposta
              </Button>
            ) : (
              <div className="space-y-5 animate-in slide-in-from-top-1 duration-200">
                {/* Explanation block */}
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100/60 shadow-3xs">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-indigo-50 border border-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
                      <Lightbulb className="w-3.5 h-3.5" />
                    </div>
                    <h5 className="text-[10px] font-black uppercase tracking-wider text-slate-800">
                      Explicação Didática
                    </h5>
                  </div>
                  <p className="text-slate-600 text-xs md:text-sm font-semibold leading-relaxed pl-1">
                    {currentQuestion.explanation}
                  </p>
                </div>

                {/* Next Question button */}
                <Button
                  onClick={handleNextQuestion}
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs px-5 shadow-xs transition-all flex items-center justify-center gap-1.5"
                >
                  <span>
                    {currentIdx < questions.length - 1 ? "Próxima Questão" : "Finalizar Exercício"}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
